/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { FormGrid, SelectField, TextAreaField } from '../../Components/Form';
import { formatNumber } from '../../utils/format';
import StatusBadge from '../../Components/Common/StatusBadge';

const IncotermLocationList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalIncotermLocation, setModalIncotermLocation] = useState(false);
  const [incotermLocationFormData, setIncotermLocationFormData] = useState({
    incotermRouteId: '',
    locationId: '',
    description: '',
    isLpgCase: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [incotermRoutes, setIncotermRoutes] = useState([]);
  const [selectedIncotermRoute, setSelectedIncotermRoute] = useState('');
  const [selectedIncotermType, setSelectedIncotermType] = useState('');
  const [selectedLpgCase, setSelectedLpgCase] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/incoterm-location/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Incoterm Location deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        const filters = {
          incotermRouteId: selectedIncotermRoute,
          incotermId: selectedIncotermType,
          searchText: search,
          isLpgCase: selectedLpgCase,
        };
        fetchData(currentPage, rowsPerPage, token, filters);
      } catch (err) {
        notify.apiError(err, 'delete incoterm location');
      }
    }
  };

  const columns = [
    {
      id: 'name',
      name: 'Location Name',
      selector: row => row.name,
      sortable: true,
      width: '20%',
      cell: row => <span className='to-title'>{row.name || '-'}</span>,
    },
    {
      id: 'incotermRoute',
      name: 'Incoterm Route',
      selector: row => row.incotermRoute?.name,
      sortable: true,
      width: '20%',
      cell: row => (
        <span className='pv-desc'>{row.incotermRoute?.name || '-'}</span>
      ),
    },
    {
      id: 'incoterm',
      name: 'Incoterm',
      selector: row => row.incotermRoute?.incoterm?.name,
      sortable: true,
      width: '15%',
      cell: row =>
        row.incotermRoute?.incoterm?.name ? (
          <span className='to-tag'>{row.incotermRoute.incoterm.name}</span>
        ) : (
          '-'
        ),
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '25%',
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      id: 'isLpgCase',
      name: 'LPG Case',
      selector: row => row.isLpgCase,
      sortable: true,
      width: '10%',
      cell: row =>
        row.isLpgCase === null || row.isLpgCase === undefined ? (
          '-'
        ) : (
          <StatusBadge
            tone={row.isLpgCase ? 'success' : 'muted'}
            label={row.isLpgCase ? 'Yes' : 'No'}
          />
        ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '10%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Incoterm Location';

  const handleChange = e => {
    const { name, value } = e.target;
    setIncotermLocationFormData({ ...incotermLocationFormData, [name]: value });
  };

  // API returns restrictions with 4 decimals (e.g. 110000.0000, 12.5000).
  const formatRestriction = value => formatNumber(value);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    const filters = {
      incotermRouteId: selectedIncotermRoute,
      incotermId: selectedIncotermType,
      searchText: search,
      isLpgCase: selectedLpgCase,
    };
    fetchData(currentPage, rowsPerPage, token, filters);
  }, [
    currentPage,
    rowsPerPage,
    token,
    selectedIncotermRoute,
    selectedIncotermType,
    selectedLpgCase,
    search,
  ]);

  useEffect(() => {
    fetchIncotermRoutes(token);
    fetchLocations(token);
  }, [token]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const handleIncotermRouteFilterChange = value => {
    setSelectedIncotermRoute(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleIncotermTypeFilterChange = value => {
    setSelectedIncotermType(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleLpgCaseFilterChange = value => {
    setSelectedLpgCase(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const clearAllFilters = () => {
    setFilterText('');
    setSearch('');
    setSelectedIncotermRoute('');
    setSelectedLpgCase('');
    setSelectedIncotermType('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const fetchData = async (currentPage, rowsPerPage, token, filters = {}) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });

      // Add filter parameters if they exist
      if (filters.incotermRouteId) {
        queryParams.append('IncotermRouteId', filters.incotermRouteId);
      }
      if (filters.incotermId) {
        queryParams.append('IncotermId', filters.incotermId);
      }
      if (filters.searchText) {
        queryParams.append('Search', filters.searchText);
      }
      if (filters.isLpgCase) {
        queryParams.append('IsLpgCase', filters.isLpgCase);
      }

      const url = `${api.API_URL}/incoterm-location?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const fetchIncotermRoutes = async token => {
    try {
      const response = await fetch(
        `${api.API_URL}/incoterm/get-incoterm-route`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setIncotermRoutes(Array.isArray(items) ? items : []);
    } catch (error) {
      notify.apiError(error, 'load incoterm routes');
      setIncotermRoutes([]);
    }
  };

  const fetchLocations = async token => {
    try {
      const queryParams = new URLSearchParams({
        PageNumber: 1,
        PageSize: 300,
        SortBy: 'Name',
        IsDescending: false,
      });

      const response = await fetch(
        `${api.API_URL}/location?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setLocationOptions(
        (Array.isArray(items) ? items : []).map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } catch (error) {
      notify.apiError(error, 'load locations');
      setLocationOptions([]);
    }
  };

  const handlePageChange = useCallback(page => setCurrentPage(page), []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const incotermRouteOptions = incotermRoutes.map(route => ({
    value: route.id,
    label: `${route.incoterm?.name} - ${route.name}`,
  }));
  // Unique incoterm types, derived from the routes lookup already loaded.
  const incotermTypeOptions = [
    ...new Map(
      incotermRoutes
        .filter(route => route.incotermId || route.incoterm?.id)
        .map(route => {
          const id = route.incotermId || route.incoterm.id;
          return [id, { value: id, label: route.incoterm?.name || id }];
        })
    ).values(),
  ];
  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    selectedIncotermRoute && {
      key: 'route',
      label: 'Incoterm Route',
      value:
        incotermRouteOptions.find(o => o.value === selectedIncotermRoute)
          ?.label ?? selectedIncotermRoute,
      onRemove: () => handleIncotermRouteFilterChange(''),
    },
    selectedLpgCase && {
      key: 'lpg',
      label: 'LPG Case',
      value: selectedLpgCase === 'true' ? 'Yes' : 'No',
      onRemove: () => handleLpgCaseFilterChange(''),
    },
    selectedIncotermType && {
      key: 'incoterm',
      label: 'Incoterm',
      value:
        incotermTypeOptions.find(o => o.value === selectedIncotermType)
          ?.label ?? selectedIncotermType,
      onRemove: () => handleIncotermTypeFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const handleNewModal = () => {
    setIncotermLocationFormData({
      incotermRouteId: '',
      locationId: '',
      description: '',
      isLpgCase: null,
      id: null,
    });
    incotermLocationModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchIncotermLocationDetail(mid);

    incotermLocationModal();
  };

  const incotermLocationModal = () => {
    setModalIncotermLocation(!modalIncotermLocation);
  };

  const selectedFieldsIncotermLocation = {
    incotermRouteId: incotermLocationFormData.incotermRouteId,
    locationId: incotermLocationFormData.locationId,
    description: incotermLocationFormData.description,
    isLpgCase: incotermLocationFormData.isLpgCase,
  };

  const handleAddDataIncotermLocation = async () => {
    try {
      await axios.post(
        `${api.API_URL}/incoterm-location`,
        selectedFieldsIncotermLocation,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Incoterm Location added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      const filters = {
        incotermRouteId: selectedIncotermRoute,
        incotermId: selectedIncotermType,
        searchText: search,
        isLpgCase: selectedLpgCase,
      };
      fetchData(currentPage, rowsPerPage, token, filters);
    }
  };

  const fetchIncotermLocationDetail = async mid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/incoterm-location/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      // The global axios interceptor (src/helpers/api_helper.js) already unwraps
      // `response.data`, so `response` is the `{ success, message, data }` envelope.
      // Peel one more level, tolerating either shape.
      const data = response?.data?.data ?? response?.data ?? response;
      setIncotermLocationFormData({
        incotermRouteId: data.incotermRouteId || '',
        locationId: data.locationId || '',
        description: data.description || '',
        isLpgCase: data.isLpgCase,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
      notify.apiError(error, 'fetch incoterm location details');
    }
  };

  const handleUpdDataIncotermLocation = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/incoterm-location/${mid}`,
        selectedFieldsIncotermLocation,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      const filters = {
        incotermRouteId: selectedIncotermRoute,
        incotermId: selectedIncotermType,
        searchText: search,
        isLpgCase: selectedLpgCase,
      };
      fetchData(currentPage, rowsPerPage, token, filters);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this incoterm location?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete incoterm location'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Incoterm Location',
                      to: '/apps-incoterm-location-view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-map-pin-2-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Incoterm Location</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the locations available for each incoterm
                        route.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, {
                            incotermRouteId: selectedIncotermRoute,
                            incotermId: selectedIncotermType,
                            searchText: search,
                            isLpgCase: selectedLpgCase,
                          })
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Incoterm Location
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search incoterm locations...'
                      ariaLabel='Search incoterm locations'
                    />
                    <ListFilterSelect
                      label='Incoterm'
                      value={selectedIncotermType}
                      onChange={handleIncotermTypeFilterChange}
                      allLabel='All Incoterms'
                      options={incotermTypeOptions}
                    />
                    <ListFilterSelect
                      label='Incoterm Route'
                      value={selectedIncotermRoute}
                      onChange={handleIncotermRouteFilterChange}
                      allLabel='All Incoterm Routes'
                      options={incotermRouteOptions}
                    />
                    <ListFilterSelect
                      label='LPG Case'
                      value={selectedLpgCase}
                      onChange={handleLpgCaseFilterChange}
                      allLabel='All'
                      options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearAllFilters}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} incoterm locations`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No incoterm location found'
                          emptyHint='No incoterm locations have been registered yet. Entries added here pair an incoterm with a location.'
                          filteredHint='No incoterm location matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearAllFilters}
                          action={{
                            label: 'Create New Incoterm Location',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalIncotermLocation}
            role='dialog'
            autoFocus={true}
            centered
            id='addIncotermLocationModal'
            toggle={incotermLocationModal}
            // data-bs-backdrop='static'
            // size='lg'
            // style={{ maxWidth: '960px' }}
            data-bs-backdrop='static'
            size='lg'
            zIndex={1060}
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalIncotermLocation(!modalIncotermLocation);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {incotermLocationFormData.id !== null
                  ? 'Edit Incoterm Location'
                  : 'Add Incoterm Location'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={2}>
                <SelectField
                  label='Incoterm route'
                  name='incotermRouteId'
                  options={incotermRoutes.map(route => ({
                    value: route.id,
                    label: `${route.incoterm?.name} - ${route.name}`,
                  }))}
                  value={incotermLocationFormData.incotermRouteId}
                  onChange={value =>
                    setIncotermLocationFormData(prev => ({
                      ...prev,
                      incotermRouteId: value ?? '',
                    }))
                  }
                />
                <SelectField
                  label='LPG case'
                  name='isLpgCase'
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={
                    incotermLocationFormData.isLpgCase === null ||
                    incotermLocationFormData.isLpgCase === undefined
                      ? ''
                      : incotermLocationFormData.isLpgCase.toString()
                  }
                  onChange={value =>
                    setIncotermLocationFormData(prev => ({
                      ...prev,
                      isLpgCase: value === null ? null : value === 'true',
                    }))
                  }
                />
                <SelectField
                  span='full'
                  label='Location'
                  name='locationId'
                  options={locationOptions}
                  value={incotermLocationFormData.locationId}
                  onChange={value =>
                    setIncotermLocationFormData(prev => ({
                      ...prev,
                      locationId: value || '',
                    }))
                  }
                />
                <TextAreaField
                  span='full'
                  label='Description'
                  name='description'
                  value={incotermLocationFormData.description}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalIncotermLocation(!modalIncotermLocation);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (incotermLocationFormData.id !== null
                    ? handleUpdDataIncotermLocation(incotermLocationFormData.id)
                    : handleAddDataIncotermLocation());
                  setModalIncotermLocation(false);
                }}
              >
                {incotermLocationFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default IncotermLocationList;
